"use client";

import { useState } from "react";
import {
    Pencil,
    ChevronLeft,
    ChevronRight,
    Check,
    // GalleryHorizontalEnd,
} from "lucide-react";

// Import animated icons
import { AnimateIcon } from "@/components/animate-ui/icons/icon";
import { Copy } from "@/components/animate-ui/icons/copy";
import { ThumbsUp } from "@/components/animate-ui/icons/thumbs-up";
import { ThumbsDown } from "@/components/animate-ui/icons/thumbs-down";
import { RefreshCw } from "@/components/animate-ui/icons/refresh-cw";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type MessageActionsProps = {
    role: "user" | "assistant";
    messageId: string;
    onCopy: () => void;
    onEdit?: () => void;
    onFeedback?: (type: "positive" | "negative") => void;
    onRecreate?: () => void;
    onNextResponse?: () => void;
    onPreviousResponse?: () => void;
    onViewInCanvas?: () => void;
    currentResponseIndex?: number;
    totalResponses?: number;
};

const MessageActionCard = ({
    role,
    onCopy,
    onEdit,
    onFeedback,
    onRecreate,
    onNextResponse,
    onPreviousResponse,
    // onViewInCanvas,
    currentResponseIndex = 1,
    totalResponses = 1,
}: MessageActionsProps) => {
    const [copied, setCopied] = useState(false);
    const [feedback, setFeedback] = useState<"positive" | "negative" | null>(null);

    const handleCopy = () => {
        onCopy();
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const handleFeedback = (type: "positive" | "negative") => {
        if (onFeedback) {
            onFeedback(type);
            setFeedback(type);
        }
    };

    if (role === "user") {
        return (
            <div className={cn("mt-1 flex items-center justify-end gap-1 transition-opacity")}>
                <TooltipProvider>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <AnimateIcon animateOnHover>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
                                    <Pencil className="h-3.5 w-3.5" />
                                    <span className="sr-only">Edit</span>
                                </Button>
                            </AnimateIcon>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <p>Edit</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <AnimateIcon animateOnHover>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy}>
                                    {copied ? (
                                        <Check className="h-3.5 w-3.5 text-green-500" />
                                    ) : (
                                        <Copy size={14} animation="default" />
                                    )}
                                    <span className="sr-only">{copied ? "Copied!" : "Copy message"}</span>
                                </Button>
                            </AnimateIcon>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <p>{copied ? "Copied!" : "Copy"}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        );
    }

    return (
        <div className="mt-1 flex items-center gap-1">
            <div className="flex items-center">
                <TooltipProvider>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <AnimateIcon animateOnHover>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 transition-colors"
                                    onClick={() => handleFeedback("positive")}
                                >
                                    <ThumbsUp
                                        animation="default-loop"
                                        className={`h-3.5 w-3.5 ${feedback === "positive" ? "text-green-600" : ""}`}
                                    />
                                    <span className="sr-only">Thumbs up</span>
                                </Button>
                            </AnimateIcon>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <p>Thumbs up</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <AnimateIcon animateOnHover>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 transition-colors"
                                    onClick={() => handleFeedback("negative")}
                                >
                                    <ThumbsDown
                                        size={14}
                                        animation="default-loop"
                                        className={feedback === "negative" ? "text-red-600" : ""}
                                    />
                                    <span className="sr-only">Thumbs down</span>
                                </Button>
                            </AnimateIcon>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <p>Thumbs down</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            <div className="mx-1 h-4 w-px bg-border" />

            <div className="flex items-center">
                {/* {onViewInCanvas && (
                    <TooltipProvider>
                        <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onViewInCanvas}>
                                    <GalleryHorizontalEnd className="h-3.5 w-3.5" />
                                    <span className="sr-only">Open Canvas</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                                <p>Open Canvas</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )} */}

                <TooltipProvider>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <AnimateIcon animateOnHover>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRecreate}>
                                    <RefreshCw size={14} animation="rotate" />
                                    <span className="sr-only">Recreate</span>
                                </Button>
                            </AnimateIcon>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <p>Recreate</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                    <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                            <AnimateIcon animateOnHover>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy}>
                                    {copied ? (
                                        <Check className="h-3.5 w-3.5 text-green-500" />
                                    ) : (
                                        <Copy size={14} animation="default" />
                                    )}
                                    <span className="sr-only">{copied ? "Copied!" : "Copy message"}</span>
                                </Button>
                            </AnimateIcon>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <p>{copied ? "Copied!" : "Copy"}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            {totalResponses > 1 && (
                <>
                    <div className="mx-1 h-4 w-px bg-border" />

                    <div className="flex items-center">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={onPreviousResponse}
                            disabled={currentResponseIndex <= 1}
                        >
                            <ChevronLeft className="h-3.5 w-3.5" />
                            <span className="sr-only">Previous response</span>
                        </Button>

                        <span className="mx-1 text-xs text-muted-foreground">
                            {currentResponseIndex}/{totalResponses}
                        </span>

                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={onNextResponse}
                            disabled={currentResponseIndex >= totalResponses}
                        >
                            <ChevronRight className="h-3.5 w-3.5" />
                            <span className="sr-only">Next response</span>
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default MessageActionCard;
